import { DeleteOutlined, EyeOutlined, PlusOutlined, SyncOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Col, Descriptions, Drawer, Form, Input, InputNumber, Popconfirm, Row, Space, Spin, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { weddingHallService } from "../services/weddingHall.service";
import { nvrService } from "@/modules/nvr/services/nvr.service";

const { Title } = Typography;

export function WeddingHallDetailPage() {
	const { id } = useParams();
	const hallId = Number(id);
	const navigate = useNavigate();
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	const [drawerOpen, setDrawerOpen] = useState(false);
	const [editingNvrId, setEditingNvrId] = useState<number | null>(null);
	const [form] = Form.useForm();

	// ==================== Data ====================
	const { data, isLoading } = useQuery({
		queryKey: ["wedding-hall", id],
		queryFn: () => weddingHallService.get(hallId),
		enabled: !!id,
	});

	const { data: nvrsData, isLoading: nvrsLoading } = useQuery({
		queryKey: ["nvrs", { wedding_hall_id: hallId }],
		queryFn: () => nvrService.list({ wedding_hall_id: hallId, per_page: 100 }),
		enabled: !!id,
	});

	// ==================== Mutations ====================
	const saveNvrMutation = useMutation({
		mutationFn: (values: Record<string, unknown>) => {
			const payload = { ...values, wedding_hall_id: hallId };
			return editingNvrId ? nvrService.update(editingNvrId, payload) : nvrService.create(payload);
		},
		onSuccess: () => {
			message.success(editingNvrId ? "NVR yangilandi" : "NVR qo'shildi");
			closeDrawer();
			queryClient.invalidateQueries({ queryKey: ["nvrs", { wedding_hall_id: hallId }] });
			queryClient.invalidateQueries({ queryKey: ["wedding-hall", id] });
		},
		onError: () => message.error("Xatolik"),
	});

	const deleteNvrMutation = useMutation({
		mutationFn: (nvrId: number) => nvrService.remove(nvrId),
		onSuccess: () => {
			message.success("NVR o'chirildi");
			queryClient.invalidateQueries({ queryKey: ["nvrs", { wedding_hall_id: hallId }] });
			queryClient.invalidateQueries({ queryKey: ["wedding-hall", id] });
		},
	});

	const syncNvrMutation = useMutation({
		mutationFn: (nvrId: number) => nvrService.sync(nvrId),
		onSuccess: () => {
			message.success("Sinxronlandi");
			queryClient.invalidateQueries({ queryKey: ["nvrs", { wedding_hall_id: hallId }] });
		},
	});

	// ==================== Drawer ====================
	const openCreateNvr = () => { setEditingNvrId(null); form.resetFields(); setDrawerOpen(true); };
	const openEditNvr = (record: Record<string, unknown>) => { setEditingNvrId(record.id as number); form.setFieldsValue(record); setDrawerOpen(true); };
	const closeDrawer = () => { setDrawerOpen(false); setEditingNvrId(null); form.resetFields(); };

	// ==================== Render ====================
	if (isLoading) return <Spin size="large" className="flex justify-center mt-20" />;

	const hall = data?.data;
	if (!hall) return <div>Topilmadi</div>;

	const nvrs = nvrsData?.data?.nvrs || [];
	const stateColor: Record<string, string> = { online: "green", offline: "red", unknown: "default" };

	const nvrColumns = [
		{ title: "ID", dataIndex: "id", width: 60 },
		{
			title: "Nomi", dataIndex: "name",
			render: (v: string, r: Record<string, unknown>) => <a onClick={() => navigate(`/nvrs/${r.id}`)}>{v}</a>,
		},
		{ title: "IP", dataIndex: "ip" },
		{ title: "Serial", dataIndex: "serial" },
		{ title: "Holat", dataIndex: "state", render: (v: string) => <Tag color={stateColor[v]}>{v}</Tag> },
		{ title: "Sync", dataIndex: "is_sync", render: (v: number) => <Tag color={v ? "green" : "orange"}>{v ? "Ha" : "Yo'q"}</Tag> },
		{ title: "Kameralar", dataIndex: "camera_count" },
		{
			title: "Amallar", width: 160,
			render: (_: unknown, r: Record<string, unknown>) => (
				<Space>
					<Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/nvrs/${r.id}`)} title="Kameralar" />
					<Button size="small" icon={<SyncOutlined />} onClick={() => syncNvrMutation.mutate(r.id as number)} title="Sinxronlash" />
					<Button size="small" onClick={() => openEditNvr(r)}>Edit</Button>
					<Popconfirm title="O'chirish?" onConfirm={() => deleteNvrMutation.mutate(r.id as number)}>
						<Button size="small" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div>
			<Title level={3}>{hall.name}</Title>

			<Row gutter={16} className="mb-4">
				<Col span={24}>
					<Card title="Asosiy ma'lumotlar">
						<Descriptions column={3} bordered size="small">
							<Descriptions.Item label="INN">{hall.inn}</Descriptions.Item>
							<Descriptions.Item label="Telefon">{hall.phone || "—"}</Descriptions.Item>
							<Descriptions.Item label="Sig'imi">{hall.capacity || "—"}</Descriptions.Item>
							<Descriptions.Item label="Manzil" span={2}>{hall.address || "—"}</Descriptions.Item>
							<Descriptions.Item label="Holat">
								<Tag color={hall.work_status === "working" ? "green" : "orange"}>{hall.work_status}</Tag>
							</Descriptions.Item>
							<Descriptions.Item label="Viloyat">{hall.region?.title || "—"}</Descriptions.Item>
							<Descriptions.Item label="Tuman">{hall.district?.title || "—"}</Descriptions.Item>
							<Descriptions.Item label="Mahalla">{hall.mahalla?.title || "—"}</Descriptions.Item>
							<Descriptions.Item label="Narxi">{hall.price ? `${hall.price} so'm` : "—"}</Descriptions.Item>
						</Descriptions>
					</Card>
				</Col>
			</Row>

			{/* ==================== NVR lar ==================== */}
			<Card
				title={`NVR qurilmalar (${nvrs.length})`}
				extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreateNvr}>NVR qo'shish</Button>}
			>
				<Table columns={nvrColumns} dataSource={nvrs} rowKey="id" loading={nvrsLoading} pagination={false} size="small" />
			</Card>

			{/* ==================== NVR Drawer ==================== */}
			<Drawer
				title={editingNvrId ? "NVR tahrirlash" : "Yangi NVR"}
				open={drawerOpen}
				onClose={closeDrawer}
				width={480}
				extra={
					<Space>
						<Button onClick={closeDrawer}>Bekor qilish</Button>
						<Button type="primary" onClick={() => form.submit()} loading={saveNvrMutation.isPending}>Saqlash</Button>
					</Space>
				}
			>
				<Form form={form} layout="vertical" onFinish={(v) => saveNvrMutation.mutate(v)}>
					<Form.Item name="name" label="Nomi" rules={[{ required: true }]}>
						<Input placeholder="NVR-1" />
					</Form.Item>
					<Form.Item name="ip" label="IP manzil">
						<Input placeholder="192.168.1.1" />
					</Form.Item>
					<Form.Item name="serial" label="Serial raqam">
						<Input />
					</Form.Item>
					<Form.Item name="device_id" label="Device ID" rules={[{ required: true }]}>
						<Input />
					</Form.Item>
					<Form.Item name="secret_key" label="Secret Key" rules={[{ required: true }]}>
						<Input />
					</Form.Item>
					<Form.Item name="brand" label="Brand">
						<Input placeholder="hikvision" />
					</Form.Item>
					<Form.Item name="model" label="Model">
						<Input />
					</Form.Item>
					<Form.Item name="channel_count" label="Kanal soni">
						<InputNumber className="w-full" />
					</Form.Item>
				</Form>
			</Drawer>
		</div>
	);
}
